/* eslint-disable @typescript-eslint/no-unused-vars */
import type { DocumentRetentionDays } from "./document-retention"
import { api, apiWithoutAuth } from "@/shared/lib/axios/axios.api"
import {
  ANONYMOUS_PROJECTS_API_PREFIX,
  API_PREFIX,
  FEEDBACK_API_PREFIX,
  GLOSSARY_API_PREFIX,
  MT_API_PREFIX,
} from "@/shared/lib/axios/axios.constants"
import { handleAxiosError } from "@/shared/lib/axios/axios.lib"
import { capitalizeFirstLetter } from "@/shared/lib/parsers/parsers"
import { getAccessToken } from "@/shared/lib/token/token"
import { createAuthenticatedEventSource } from "@/shared/lib/event-source/event-source"
import { CATXliffExportRejectedError } from "./cat-xliff-export"
import { normalizeCatSegmentQE } from "./cat-qe"
import {
  AssemblyStatusResponse,
  CATAssignedWorkspacePage,
  CATQAResponse,
  CATQAFeedbackReason,
  CATQARun,
  CATQARunIssue,
  CATQARunResponse,
  CATSegmentCommentAddResponse,
  CATSegmentCommentsResponse,
  CATSegmentEditHistoryResponse,
  CATEditReason,
  CATSegmentReviewStatus,
  CATXliffExportDownloadResponse,
  CATXliffExportFormat,
  CATXliffExportResponse,
  CATXliffImportRecord,
  CATXliffImportResponse,
  CATSegmentReviewUpdateResponse,
  CATWorkflowInvitationResponse,
  CATWorkflowResponse,
  CATWorkflowRole,
  CATWorkspaceResponse,
  DocumentAssemblyResponse,
  DocumentTranslationProgress,
  DocumentVersionInfo,
  CreateGlossaryReq,
  DomainTypeEnum,
  FeedbackForm,
  FileQueue,
  Glossary,
  GlossarySnakecase,
  MachineTranslationForm,
  PredictCostRequest,
  PredictCostResponse,
  PresignedUrlType,
  SmartGlossary,
  TranslationStatusEnum,
} from "./translation.types"

const DOCUMENT_JOB_METADATA_TIMEOUT_MS = 15_000

export const getAssignedCatWorkspaces = async ({
  page,
  itemsPerPage,
}: {
  page: number
  itemsPerPage: number
}) => {
  const response = await api.get<CATAssignedWorkspacePage>(
    `${API_PREFIX}/cat-workspaces/assigned`,
    {
      params: { page, itemsPerPage },
      // The global navigation probes this endpoint in the background. The
      // assigned-work page already renders its own error state.
      suppressServerErrorAlert: true,
    }
  )

  return response.data
}

const deserializeGlossary = ({
  id,
  name,
  owner_id,
  mappings,
  source_language,
  target_language,
}: GlossarySnakecase): Glossary => ({
  id,
  name,
  ownerId: owner_id,
  mappings,
  sourceLanguage: source_language,
  targetLanguage: target_language,
})

export const getPresignedUrl = async (ext: string, userId?: string) => {
  try {
    const response = await (
      userId ? apiWithoutAuth : api
    ).post<PresignedUrlType>(
      userId
        ? `${ANONYMOUS_PROJECTS_API_PREFIX}/presigned-url`
        : `${MT_API_PREFIX}/presigned-url`,
      {
        ext: capitalizeFirstLetter(ext),
        ...(userId && {
          email: userId,
        }),
      }
    )

    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getGlossaryList = async () => {
  try {
    // Glossary terms are dynamic object keys, not API field names. Preserve the
    // raw response keys so terms such as "Loss of Coolant Accident" and
    // "Loss-Of-Coolant Accident" cannot collapse into the same key. The normal
    // API client still provides authorization and token refresh behavior.
    const response = await api.get<GlossarySnakecase[]>(GLOSSARY_API_PREFIX, {
      preserveResponseKeys: true,
    })

    return response.data.map(deserializeGlossary)
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getGlossaryDetail = async ({
  glossaryId,
}: {
  glossaryId: string
}) => {
  try {
    const response = await apiWithoutAuth.get<GlossarySnakecase>(
      `${GLOSSARY_API_PREFIX}/${glossaryId}`,
      { headers: { Authorization: `Bearer ${getAccessToken()}` } }
    )

    return deserializeGlossary(response.data)
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const editMappingsOfGlossary = async ({
  glossaryId,
  mappings,
}: {
  glossaryId: string
  mappings: Record<string, string>
}) => {
  const response = await apiWithoutAuth.patch<string>(
    GLOSSARY_API_PREFIX,
    {
      glossary_id: glossaryId,
      mappings,
    },
    { headers: { Authorization: `Bearer ${getAccessToken()}` } }
  )

  return response.data
}

export const deleteFile = async ({
  jobId,
  showToast,
}: {
  jobId: string
  showToast: boolean
}) => {
  const response = await api.patch<string>(`${MT_API_PREFIX}/${jobId}`)

  return { data: response.data, showToast }
}

export const translateFile = async ({
  translationForm,
  sourceTitle,
  glossaryId,
  posteditPromptCode,
  retentionDays,
}: {
  translationForm: MachineTranslationForm
  sourceTitle: string
  glossaryId?: string
  posteditPromptCode?: string
  retentionDays?: DocumentRetentionDays
}) => {
  const { sourceContent, domain, ...rest } = translationForm
  const sourceLanguage = rest.sourceLanguage
  const targetLanguage = rest.targetLanguage

  if (sourceLanguage === targetLanguage) {
    return Promise.reject(new Error("SOURCE_TARGET_SAME"))
  }

  const body = {
    ...rest,
    domain: domain === DomainTypeEnum.General ? "business" : domain,
    sourceTitle,
    retentionDays,
    ...(glossaryId && { glossaryId }),
    ...(posteditPromptCode && { posteditPromptCode }),
  }

  if (import.meta.env.DEV) {
    console.log(
      "[translateFile] POST mp-jobs request body (before snake_case):",
      {
        sourceLanguage: body.sourceLanguage,
        targetLanguage: body.targetLanguage,
        domain: body.domain,
        sourceTitle: body.sourceTitle,
        inputPath: body.inputPath,
      }
    )
  }

  const response = await api.post<string>(`${MT_API_PREFIX}/mp-jobs`, body)

  return response.data
}

export const translateText = async ({
  translationForm,
  glossaryId,
  posteditPromptCode,
}: {
  translationForm: MachineTranslationForm
  glossaryId?: string
  posteditPromptCode?: string
}) => {
  const { inputPath, domain, ...rest } = translationForm
  const response = await api.post<string>(`${MT_API_PREFIX}/text-mp-jobs`, {
    ...rest,
    domain: domain === DomainTypeEnum.General ? "business" : domain,
    // Which client placed the job. After the form spread so a stray field on
    // the form can never relabel console traffic.
    channel: "web",
    ...(glossaryId && { glossaryId }),
    ...(posteditPromptCode && { posteditPromptCode }),
  })

  return response.data
}

export const createGlossary = async ({ form }: { form: CreateGlossaryReq }) => {
  const response = await api.post<string>(`${GLOSSARY_API_PREFIX}`, form)

  return response.data
}

export const deleteGlossary = async ({
  glossaryId,
}: {
  glossaryId: string
}) => {
  const response = await api.delete<never>(
    `${GLOSSARY_API_PREFIX}/${glossaryId}`
  )

  return response.data
}

// event source
export const getEventSource = ({
  token,
  jobId,
}: {
  token: string
  jobId?: string
}) => {
  return createAuthenticatedEventSource({
    url: `${import.meta.env.VITE_BASE_API_URL}${MT_API_PREFIX}/progress${
      jobId ? `/text-jobs/${jobId}` : "/document-jobs"
    }`,
    token,
    stream: jobId ? "text_translation_progress" : "document_job_progress",
  })
}

interface RawDocumentJobProgress {
  source_title: string
  source_language: FileQueue["sourceLanguage"]
  target_language: FileQueue["targetLanguage"]
  domain: FileQueue["domain"]
  job_id: string | number
  is_deleted: boolean
  input_path: string
  status: FileQueue["status"]
  error_code: number | null
  smart_glossary_extracted: boolean
  processed_segments?: number
  total_segments?: number
  translation_phase?: FileQueue["translationPhase"]
}

export const parseDocumentJobProgressEvent = (data: string): FileQueue => {
  const raw = JSON.parse(data) as Partial<RawDocumentJobProgress> & {
    error?: unknown
  }

  if (
    raw.error ||
    raw.job_id === undefined ||
    typeof raw.source_title !== "string" ||
    typeof raw.source_language !== "string" ||
    typeof raw.target_language !== "string" ||
    typeof raw.domain !== "string" ||
    typeof raw.input_path !== "string" ||
    typeof raw.status !== "string" ||
    typeof raw.is_deleted !== "boolean" ||
    typeof raw.smart_glossary_extracted !== "boolean"
  ) {
    throw new Error("Document job progress event is missing metadata")
  }

  const jobId = String(raw.job_id)

  return {
    id: jobId,
    jobId,
    sourceTitle: raw.source_title,
    sourceLanguage: raw.source_language,
    targetLanguage: raw.target_language,
    domain: raw.domain,
    inputPath: raw.input_path,
    status: raw.status,
    isDeleted: raw.is_deleted,
    errorCode: raw.error_code ?? null,
    smartGlossaryExtracted: raw.smart_glossary_extracted,
    processedSegments: raw.processed_segments,
    totalSegments: raw.total_segments,
    translationPhase: raw.translation_phase,
  }
}

export const sendFeedback = async ({ data }: { data: FeedbackForm }) => {
  const response = await api.post<FeedbackForm>(
    `${FEEDBACK_API_PREFIX}/feedback`,
    data
  )

  return response.data
}

export const getDocumentJob = ({ jobId }: { jobId: string }) =>
  new Promise<FileQueue>((resolve, reject) => {
    const eventSource = createAuthenticatedEventSource({
      url: `${import.meta.env.VITE_BASE_API_URL}${MT_API_PREFIX}/progress/document-jobs/${jobId}`,
      token: getAccessToken(),
      stream: "document_job_metadata",
    })
    let settled = false

    const timeoutId = globalThis.setTimeout(() => {
      finish(() => reject(new Error("Document job metadata request timed out")))
    }, DOCUMENT_JOB_METADATA_TIMEOUT_MS)

    const finish = (complete: () => void) => {
      if (settled) return
      settled = true
      globalThis.clearTimeout(timeoutId)
      eventSource.close()
      complete()
    }

    eventSource.onmessage = (event) => {
      try {
        const payload: unknown = JSON.parse(event.data)
        if (
          typeof payload === "object" &&
          payload !== null &&
          "error" in payload &&
          typeof payload.error === "string"
        ) {
          const errorMessage = payload.error
          finish(() => reject(new Error(errorMessage)))
          return
        }
      } catch {
        // The metadata parser below owns validation for non-JSON frames.
      }

      try {
        const documentJob = parseDocumentJobProgressEvent(event.data)
        finish(() => resolve(documentJob))
      } catch {
        // Progress endpoints may emit a partial status or heartbeat frame
        // before the complete job object. Keep waiting for a valid metadata
        // frame; the bounded timeout still closes an unusable stream.
      }
    }

    eventSource.onerror = () => {
      finish(() => reject(new Error("Failed to load document job metadata")))
    }
  })

// Raw response shape — what the backend actually serializes (snake_case).
// Declared inline because we manually map it; the global SmartGlossary type
// stays camelCase for consumers.
interface RawSmartGlossaryItem {
  id: string
  source_term: string
  target_term: string
  is_custom_glossary: boolean
  voting_log: {
    batch: number
    candidates: Record<string, number>
    is_custom_glossary: boolean
    selected: string
    src: string
  } | null
}

interface RawSmartGlossaryResponse {
  job_id: string
  source_language: SmartGlossary["sourceLanguage"]
  target_language: SmartGlossary["targetLanguage"]
  glossary: RawSmartGlossaryItem[]
}

export const getSmartGlossaries = async ({ jobId }: { jobId: string }) => {
  try {
    // Bypass the global camelize interceptor on `api`. humps recursively
    // walks every object key, which mangles `voting_log.candidates` —
    // those keys are user-facing translation phrases like
    // "Internship Agreement", not API field names, so the auto-camelize
    // turns them into "internshipAgreement" and the alternative picker
    // displays gibberish. We use `apiWithoutAuth` (no interceptors), pass
    // the access token explicitly, and map fields by hand so `candidates`
    // is preserved verbatim.
    const response = await apiWithoutAuth.get<RawSmartGlossaryResponse>(
      `${API_PREFIX}/document-jobs/${jobId}/smart-glossaries`,
      { headers: { Authorization: `Bearer ${getAccessToken()}` } }
    )
    const raw = response.data
    const result: SmartGlossary = {
      jobId: raw.job_id,
      sourceLanguage: raw.source_language,
      targetLanguage: raw.target_language,
      glossary: (raw.glossary ?? []).map((g) => ({
        id: g.id,
        sourceTerm: g.source_term,
        targetTerm: g.target_term,
        isCustomGlossary: g.is_custom_glossary,
        votingLog: g.voting_log
          ? {
              batch: g.voting_log.batch,
              candidates: g.voting_log.candidates,
              isCustomGlossary: g.voting_log.is_custom_glossary,
              selected: g.voting_log.selected,
              src: g.voting_log.src,
            }
          : null,
      })),
    }
    return result
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const updateSmartGlossary = async ({
  glossaryId,
  targetTerm,
}: {
  glossaryId: string
  targetTerm: string
}) => {
  // Per the redesigned PUT API: the path id is the per-row glossary id and
  // the body is `{ target_term }` for a single term, not the previous bulk
  // `{ mappings }` payload. The interceptor decamelizes { targetTerm } →
  // { target_term } on the wire automatically.
  const response = await api.put<boolean>(
    `${API_PREFIX}/smart-glossaries/${glossaryId}`,
    { targetTerm }
  )

  return response.data
}

export const mergeSmartGlossaryIntoExistingGlossary = async ({
  glossaryId,
  jobId,
}: {
  glossaryId: string
  jobId: string
}) => {
  const response = await api.post<never>(`${GLOSSARY_API_PREFIX}/merge`, {
    jobId,
    glossaryId,
  })

  return response.data
}

export const predictCost = async (data: PredictCostRequest) => {
  try {
    const response = await api.post<PredictCostResponse>(
      "/task-management/api/v1/external/mt/text/predict-cost",
      data
    )

    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

// Media Translation API
export const startMediaTranslation = async ({
  type,
  inputPath,
  sourceLanguage,
  targetLanguage,
  domain,
}: {
  type: "video" | "audio" | "transcript"
  inputPath: string
  sourceLanguage: string
  targetLanguage: string
  domain: string
}) => {
  try {
    // Mock API - Media Translation 시작
    const response = await api.post<{ jobId: string }>(
      `${MT_API_PREFIX}/media/translate`,
      {
        type,
        inputPath,
        sourceLanguage,
        targetLanguage,
        domain,
      }
    )

    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getDocumentTranslationProgress = async ({
  jobId,
}: {
  jobId: string
}) => {
  try {
    const response = await api.get<DocumentTranslationProgress>(
      `${MT_API_PREFIX}/progress/document-jobs/${jobId}/translation`
    )

    return response.data
  } catch (error) {
    return handleAxiosError(error)
  }
}

export const getCatWorkspace = async ({
  jobId,
  compareWithPrevious = false,
  compareWith,
}: {
  jobId: string
  compareWithPrevious?: boolean
  compareWith?: string
}) => {
  if (compareWithPrevious && compareWith) {
    throw new Error(
      "compareWithPrevious and compareWith are mutually exclusive"
    )
  }
  // No try/catch here on purpose. handleAxiosError, which the neighbours use, rethrows
  // a plain Error for a string body -- and that drops the status the editor classifies
  // on, so a 401 would arrive indistinguishable from a network fault and be offered a
  // retry instead of being called a refusal. Nothing reads this error's message.
  const response = await api.get<CATWorkspaceResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace`,
    {
      // The editor polls this every 5 seconds, and the interceptor's 500 handler raises a
      // toast. Without this a server fault becomes a toast every 5 seconds; the editor
      // says so on the page instead.
      suppressServerErrorAlert: true,
      params: {
        include_style_markers: false,
        include_inline_tags: true,
        ...(compareWith
          ? { compare_with: compareWith }
          : { compare_with_previous: compareWithPrevious }),
      },
    }
  )

  return response.data
}

export const getDocumentVersions = async ({
  jobId,
  timeoutMs,
}: {
  jobId: string
  timeoutMs?: number
}) => {
  const response = await api.get<DocumentVersionInfo[]>(
    `${MT_API_PREFIX}/documents/${jobId}/versions`,
    timeoutMs ? { timeout: timeoutMs } : undefined
  )
  return response.data
}

/**
 * The /revisions/upload endpoint enforces a no-branching constraint: a
 * parent can have at most one child. If the user tries to upload a new
 * version against an older sibling that already has a child, the backend
 * returns 400 ConstraintViolated.
 *
 * This helper resolves whichever job_id the user picked to the LATEST
 * member of the lineage so the upload always extends the chain instead of
 * forking it. Falls back to the input jobId on any failure (404, network)
 * — the upload itself will then surface the real error.
 */
const REVISABLE_STATUSES = new Set([
  TranslationStatusEnum.Completed,
  TranslationStatusEnum.Failed,
  TranslationStatusEnum.TimedOut,
])

export const resolveLatestRevisionParent = async ({
  jobId,
  knownAliveJobIds,
}: {
  jobId: string
  knownAliveJobIds?: Set<string>
}): Promise<string> => {
  try {
    const versions = await getDocumentVersions({ jobId })
    if (versions.length === 0) return jobId
    // Walk from highest version down. A valid parent must:
    // 1. Exist in knownAliveJobIds (from fileQueue, which excludes deleted jobs)
    // 2. Have a revisable status (completed, failed, timed_out)
    const sorted = [...versions].sort((a, b) => b.version - a.version)
    for (const v of sorted) {
      if (knownAliveJobIds?.has(v.id) && REVISABLE_STATUSES.has(v.status)) {
        return v.id
      }
    }
    return jobId
  } catch {
    return jobId
  }
}

export const uploadDocumentRevision = async ({
  parentJobId,
  file,
  timeoutMs,
  retentionDays,
}: {
  parentJobId: string
  file: File
  timeoutMs?: number
  retentionDays?: DocumentRetentionDays
}) => {
  const formData = new FormData()
  formData.append("file", file)
  // Per the agreed BE contract, the revision endpoint receives the FULL file
  // name including its extension (unlike the new-file path, which sends an
  // extension-less source_title). The display layer normalizes this via
  // buildFileNameWithExtension so the extension isn't rendered twice.
  formData.append(
    "metadata",
    JSON.stringify({
      source_title: file.name,
      source: "web",
      retention_days: retentionDays,
    })
  )
  // Do NOT set Content-Type manually. Axios + the underlying form-data
  // serializer auto-generates `multipart/form-data; boundary=...` from the
  // FormData instance. Setting just `multipart/form-data` strips the
  // boundary parameter and the backend rejects with HTTP 400
  // "Invalid `boundary` for `multipart/form-data` request".
  const response = await api.post<string>(
    `${MT_API_PREFIX}/mp-jobs/${parentJobId}/revisions/upload`,
    formData,
    timeoutMs ? { timeout: timeoutMs } : undefined
  )
  return response.data
}

export class CATMachineTranslationArtifactUnavailableError extends Error {
  constructor(public status: number) {
    super(`MT output download failed with HTTP ${status}`)
  }
}

export const getTranslatedCATXliff = async (jobId: string) => {
  const response = await api.get<{ url: string }>(
    `${MT_API_PREFIX}/${jobId}/file-url`,
    { timeout: 15_000 }
  )
  if (!response.data.url) throw new Error("MT output URL is missing")
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 60_000)
  try {
    const artifact = await fetch(response.data.url, {
      credentials: "omit",
      signal: controller.signal,
    })
    if (!artifact.ok) {
      if (
        artifact.status >= 400 &&
        artifact.status < 500 &&
        artifact.status !== 408 &&
        artifact.status !== 425 &&
        artifact.status !== 429
      ) {
        throw new CATMachineTranslationArtifactUnavailableError(artifact.status)
      }
      throw new Error(`MT output download failed with HTTP ${artifact.status}`)
    }
    return await artifact.text()
  } finally {
    clearTimeout(timeout)
  }
}

export const requestDocumentAssembly = async ({ jobId }: { jobId: string }) => {
  const response = await api.post<DocumentAssemblyResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/assemble`
  )
  return response.data
}

const CAT_XLIFF_EXPORT_MAX_POLLS = 120
const CAT_XLIFF_EXPORT_POLL_INTERVAL_MS = 1000
const CAT_XLIFF_API_REQUEST_TIMEOUT_MS = 15_000
const CAT_XLIFF_ARTIFACT_FETCH_TIMEOUT_MS = 60_000

const waitForCatXliffPoll = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, CAT_XLIFF_EXPORT_POLL_INTERVAL_MS)
  })

const fetchCatXliffArtifact = async (downloadUrl: string) => {
  const controller = new AbortController()
  const timeoutHandle = setTimeout(
    () => controller.abort(),
    CAT_XLIFF_ARTIFACT_FETCH_TIMEOUT_MS
  )
  try {
    const response = await fetch(downloadUrl, {
      credentials: "omit",
      signal: controller.signal,
    })
    if (!response.ok) throw new Error("XLIFF artifact download failed")
    return await response.blob()
  } finally {
    clearTimeout(timeoutHandle)
  }
}

export const exportCatXliff = async ({
  jobId,
  format,
}: {
  jobId: string
  format: CATXliffExportFormat
}) => {
  const started = await api.post<CATXliffExportResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-exports/async`,
    { format },
    { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
  )
  let exportRecord = started.data.export

  for (
    let attempt = 0;
    exportRecord.status === "queued" && attempt < CAT_XLIFF_EXPORT_MAX_POLLS;
    attempt += 1
  ) {
    const status = await api.get<CATXliffExportResponse>(
      `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-exports/${exportRecord.id}`,
      { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
    )
    exportRecord = status.data.export
    if (
      exportRecord.status === "queued" &&
      attempt < CAT_XLIFF_EXPORT_MAX_POLLS - 1
    ) {
      await waitForCatXliffPoll()
    }
  }

  if (exportRecord.status === "failed") {
    if (exportRecord.errorMessage) {
      throw new CATXliffExportRejectedError(exportRecord.errorMessage)
    }
    throw new Error("XLIFF export failed")
  }
  if (exportRecord.status !== "completed") {
    throw new Error("XLIFF export timed out")
  }

  const descriptor = await api.get<CATXliffExportDownloadResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-exports/${exportRecord.id}/download`,
    { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
  )
  return {
    blob: await fetchCatXliffArtifact(descriptor.data.downloadUrl),
    fileName: descriptor.data.fileName,
    checksum: descriptor.data.checksum,
  }
}

const pollCatXliffImport = async ({
  jobId,
  importRecord,
  pendingStatus,
}: {
  jobId: string
  importRecord: CATXliffImportRecord
  pendingStatus: "queued" | "applying"
}) => {
  let current = importRecord
  for (
    let attempt = 0;
    current.status === pendingStatus && attempt < CAT_XLIFF_EXPORT_MAX_POLLS;
    attempt += 1
  ) {
    const response = await api.get<CATXliffImportResponse>(
      `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-imports/${current.id}`,
      { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
    )
    current = response.data.import
    if (
      current.status === pendingStatus &&
      attempt < CAT_XLIFF_EXPORT_MAX_POLLS - 1
    ) {
      await waitForCatXliffPoll()
    }
  }
  if (current.status === pendingStatus) {
    throw new Error("XLIFF import timed out")
  }
  return current
}

export const getCatXliffImport = async ({
  jobId,
  importId,
}: {
  jobId: string
  importId: string
}) => {
  const response = await api.get<CATXliffImportResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-imports/${importId}`,
    { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
  )
  return response.data.import
}

export const startCatXliffImport = async ({
  jobId,
  file,
}: {
  jobId: string
  file: File
}) => {
  const formData = new FormData()
  formData.append("file", file, file.name)
  const response = await api.post<CATXliffImportResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-imports`,
    formData,
    { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
  )
  return pollCatXliffImport({
    jobId,
    importRecord: response.data.import,
    pendingStatus: "queued",
  })
}

export const applyCatXliffImport = async ({
  jobId,
  importId,
  onAccepted,
}: {
  jobId: string
  importId: string
  onAccepted?: (record: CATXliffImportRecord) => void
}) => {
  const response = await api.post<CATXliffImportResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/bilingual-imports/${importId}/apply`,
    undefined,
    { timeout: CAT_XLIFF_API_REQUEST_TIMEOUT_MS }
  )
  onAccepted?.(response.data.import)
  return pollCatXliffImport({
    jobId,
    importRecord: response.data.import,
    pendingStatus: "applying",
  })
}

export const getAssemblyStatus = async ({
  jobId,
  assemblyRequestId,
}: {
  jobId: string
  assemblyRequestId: string
}) => {
  const response = await api.get<AssemblyStatusResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/assembly-status`,
    { params: { assemblyRequestId } }
  )
  return response.data
}

export const getCatSegmentQE = async ({ jobId }: { jobId: string }) => {
  const response = await api.get<unknown>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/qe`
  )
  return normalizeCatSegmentQE(response.data)
}

export const runCatQA = async ({
  jobId,
  edits,
}: {
  jobId: string
  edits: {
    segmentId: string
    newTranslatedText: string
    protectedInlineTags?: string[]
  }[]
}) => {
  const response = await api.post<CATQAResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/qa`,
    { edits }
  )
  return response.data
}

interface RawCATQARunIssue {
  segment_id: string
  segment_number: number
  source_excerpt: string
  fingerprint: string
  code: CATQARunIssue["code"]
  severity: CATQARunIssue["severity"]
  expected?: string[]
  actual?: string[]
  omitted_count?: number
}

interface RawCATQARun {
  id: string
  job_id: string
  created_by: string
  profile_key: string
  profile_version: number
  rule_version: number
  input_fingerprint: string
  resource_fingerprint: string
  segment_revisions: Record<string, number>
  state: CATQARun["state"]
  source_title?: string
  total_segments: number
  error_count: number
  warning_count: number
  ignored_count: number
  issues: RawCATQARunIssue[]
  ignored_issues?: RawCATQARunIssue[]
  created_at: string
}

interface RawCATQARunResponse {
  success: boolean
  feedback_projection_supported?: boolean
  run?: RawCATQARun
}

const deserializeCatQARunIssue = (issue: RawCATQARunIssue): CATQARunIssue => ({
  segmentId: issue.segment_id,
  segmentNumber: issue.segment_number,
  sourceExcerpt: issue.source_excerpt,
  fingerprint: issue.fingerprint,
  code: issue.code,
  severity: issue.severity,
  expected: issue.expected,
  actual: issue.actual,
  omittedCount: issue.omitted_count ?? 0,
})

const deserializeCatQARunResponse = (
  response: RawCATQARunResponse
): CATQARunResponse => {
  const feedbackProjectionSupported =
    response.feedback_projection_supported ?? false
  if (!response.run)
    return { success: response.success, feedbackProjectionSupported }
  const run = response.run
  return {
    success: response.success,
    feedbackProjectionSupported,
    run: {
      id: run.id,
      jobId: run.job_id,
      createdBy: run.created_by,
      profileKey: run.profile_key,
      profileVersion: run.profile_version,
      ruleVersion: run.rule_version,
      inputFingerprint: run.input_fingerprint,
      resourceFingerprint: run.resource_fingerprint,
      // These keys are segment identifiers, not API field names. Preserve
      // them verbatim instead of running them through the global camelizer.
      segmentRevisions: run.segment_revisions,
      state: run.state,
      sourceTitle: run.source_title,
      totalSegments: run.total_segments,
      errorCount: run.error_count,
      warningCount: run.warning_count,
      ignoredCount: run.ignored_count,
      issues: (run.issues ?? []).map(deserializeCatQARunIssue),
      ignoredIssues: (run.ignored_issues ?? []).map(deserializeCatQARunIssue),
      createdAt: run.created_at,
    },
  }
}

export const createCatQARun = async ({ jobId }: { jobId: string }) => {
  const response = await api.post<RawCATQARunResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/qa/runs`,
    undefined,
    { preserveResponseKeys: true }
  )
  return deserializeCatQARunResponse(response.data)
}

export const getLatestCatQARun = async ({ jobId }: { jobId: string }) => {
  const response = await api.get<RawCATQARunResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/qa/runs/latest`,
    { preserveResponseKeys: true }
  )
  return deserializeCatQARunResponse(response.data)
}

export const setCatQAFeedback = async ({
  jobId,
  segmentId,
  fingerprint,
  ignored,
  reason,
  comment,
  edits,
}: {
  jobId: string
  segmentId: string
  fingerprint: string
  ignored: boolean
  reason?: CATQAFeedbackReason
  comment?: string
  edits: {
    segmentId: string
    newTranslatedText: string
    protectedInlineTags?: string[]
  }[]
}) => {
  const response = await api.put<CATQAResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/qa/feedback`,
    { segmentId, fingerprint, ignored, reason, comment, edits }
  )
  return response.data
}

export const bulkEditSegments = async ({
  jobId,
  edits,
}: {
  jobId: string
  edits: {
    segmentId: string
    newTranslatedText: string
    protectedInlineTags?: string[]
    expectedRevision?: number
    editReason?: CATEditReason
  }[]
}) => {
  const response = await api.put<{
    success: boolean
    updatedCount: number
    segmentRevisions?: Record<string, number>
  }>(`${API_PREFIX}/jobs/${jobId}/cat-workspace/bulk-edit`, { edits })

  if (!response.data.success || response.data.updatedCount !== edits.length) {
    throw new Error(
      `CAT bulk edit was incomplete: expected ${edits.length} updates, received ${response.data.updatedCount}`
    )
  }

  return response.data
}

export const updateCatSegmentReview = async ({
  jobId,
  segmentId,
  status,
  selfAssign,
  locked,
}: {
  jobId: string
  segmentId: string
  status?: CATSegmentReviewStatus
  selfAssign?: boolean
  locked?: boolean
}) => {
  const response = await api.patch<CATSegmentReviewUpdateResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/segments/${segmentId}/review`,
    {
      ...(status === undefined ? {} : { status }),
      ...(selfAssign === undefined ? {} : { selfAssign }),
      ...(locked === undefined ? {} : { locked }),
    }
  )
  return response.data
}

export const upsertCatWorkflowAssignment = async ({
  jobId,
  email,
  role,
}: {
  jobId: string
  email: string
  role: CATWorkflowRole
}) => {
  const response = await api.put<CATWorkflowResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/workflow/assignments`,
    { email, role }
  )
  return response.data
}

export const deleteCatWorkflowAssignment = async ({
  jobId,
  accountId,
}: {
  jobId: string
  accountId: string
}) => {
  const response = await api.delete<CATWorkflowResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/workflow/assignments/${accountId}`
  )
  return response.data
}

export const respondToCatWorkflowInvitation = async ({
  jobId,
  accepted,
}: {
  jobId: string
  accepted: boolean
}) => {
  const response = await api.post<CATWorkflowInvitationResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/workflow/invitation`,
    { accepted }
  )
  return response.data
}

export const completeCatWorkflow = async ({ jobId }: { jobId: string }) => {
  const response = await api.post<CATWorkflowResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/workflow/complete`
  )
  return response.data
}

export const reopenCatWorkflow = async ({ jobId }: { jobId: string }) => {
  const response = await api.post<CATWorkflowResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/workflow/reopen`
  )
  return response.data
}

export const getCatSegmentComments = async ({
  jobId,
  segmentId,
}: {
  jobId: string
  segmentId: string
}) => {
  const response = await api.get<CATSegmentCommentsResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/segments/${segmentId}/comments`
  )
  return response.data
}

export const getCatSegmentEditHistory = async ({
  jobId,
  segmentId,
  beforeRevision,
}: {
  jobId: string
  segmentId: string
  beforeRevision?: number
}) => {
  const response = await api.get<CATSegmentEditHistoryResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/segments/${segmentId}/history`,
    {
      params: {
        limit: 100,
        ...(beforeRevision !== undefined && { beforeRevision }),
      },
    }
  )
  return response.data
}

export const addCatSegmentComment = async ({
  jobId,
  segmentId,
  body,
  parentCommentId,
  mentionedAccountIds,
}: {
  jobId: string
  segmentId: string
  body: string
  parentCommentId?: string
  mentionedAccountIds?: string[]
}) => {
  const response = await api.post<CATSegmentCommentAddResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/segments/${segmentId}/comments`,
    { body, parentCommentId, mentionedAccountIds: mentionedAccountIds ?? [] }
  )
  return response.data
}

export const updateCatSegmentComment = async ({
  jobId,
  segmentId,
  commentId,
  resolved,
}: {
  jobId: string
  segmentId: string
  commentId: string
  resolved: boolean
}) => {
  const response = await api.patch<CATSegmentCommentAddResponse>(
    `${API_PREFIX}/jobs/${jobId}/cat-workspace/segments/${segmentId}/comments/${commentId}`,
    { resolved }
  )
  return response.data
}

export const updateEditorTitle = async ({ title }: { title: string }) => {
  // TODO: Replace with actual API endpoint when available
  const response = await api.patch<string>(`${MT_API_PREFIX}/editor/title`, {
    title,
  })

  return response.data
}
